<script setup lang="ts">
import Button, {
  type ButtonEmits,
  type ButtonPassThroughMethodOptions,
  type ButtonProps
} from "primevue/button";
import { PresetHelper } from "@/helpers/preset.helper";
import IconComponent from "../IconComponent/IconComponent.vue";
import { IconName } from "@/constants";
import { watchEffect } from "vue";
import PermissionGateComponent from "../PermissionGateComponent/PermissionGateComponent.vue";
import type { PermissionConfig } from "@/stores/permission.store";
import { usePermissionGate } from "@/composables/UsePermissionGate";
import ProgressSpinnerComponent from "../ProgressSpinnerComponent/ProgressSpinnerComponent.vue";

type TaiButtonVariant = "primary" | "secondary" | "tertiary";
export interface TaiButtonProps extends /* @vue-ignore */ ButtonProps {
  variant?: TaiButtonVariant;
  icon?: IconName;
  label?: string;
  transparent?: boolean;
  permission?: PermissionConfig | Array<PermissionConfig>;
  loading?: boolean;
  tooltip?: string;
}

const props = defineProps<TaiButtonProps>();
const emit = defineEmits</* @vue-ignore */ ButtonEmits>();

const { permissionGate, permissionResult } = usePermissionGate(
  "button-permission-gate"
);

watchEffect(() => {
  if (props.transparent && props.outlined)
    console.warn(
      "[ButtonComponent] transparent and outlined cannot be true at the same time"
    );
});

const handleTrigger = (event: MouseEvent) => {
  if (props.permission && !permissionResult.value?.allowed) {
    permissionGate.value?.show(event);
  } else {
    emit("click", event);
  }
};

const { preset } = new PresetHelper({
  classMap: {
    tai__button_root: {
      spacing: ["px-3 py-3"],
      hover: ["hover:opacity-80"]
    },

    "tai__button_root--with-label": {
      spacing: ["min-w-[86px]"]
    },

    "tai__button_root--disabled": {
      color: ["!bg-system-light-grey !text-system-grey !border-system-bg"]
    },

    "tai__button_root--transparent": {
      border: ["!border-none !bg-transparent"],
      focus: ["focus:!ring-0 focus:!underline"],
      layout: ["!px-0"],
      typography: ["!text-left"]
    },

    "tai__button_root--outlined--disabled": {
      color: ["!bg-white !text-system-grey !border-system-light-grey"]
    },

    "tai__button_root--primary": {
      color: ["!bg-contrast-bitter-sweet !border-contrast-bitter-sweet"],
      hover: ["hover:!bg-[#e04a44] hover:!border-[#e04a44]"],
      focus: ["focus:ring-[1px] focus:!ring-contrast-bitter-sweet"]
    },

    "tai__button_root--primary--outlined": {
      color: [
        "!bg-white !border-contrast-bitter-sweet !text-contrast-bitter-sweet"
      ],
      hover: [
        "hover:!bg-contrast-peach/30 hover:!border-contrast-bitter-sweet"
      ],
      focus: ["focus:ring-[1px] focus:!ring-contrast-bitter-sweet"]
    },

    "tai__button_root--primary--transparent": {
      color: ["!text-contrast-bitter-sweet text-contrast-bitter-sweet"]
    },

    "tai__button_root--secondary": {
      color: ["!bg-contrast-emerald !border-contrast-emerald"],
      focus: ["focus:ring-[1px] focus:!ring-contrast-emerald"]
    },

    "tai__button_root--secondary--outlined": {
      color: ["!bg-white !border-contrast-emerald !text-contrast-emerald"],
      hover: ["hover:!bg-contrast-emerald-20/30"],
      focus: ["focus:ring-[1px] focus:!ring-contrast-emerald"]
    },

    "tai__button_root--secondary--transparent": {
      color: ["!text-contrast-emerald"]
    },

    "tai__button_root--tertiary": {
      spacing: ["!p-2"],
      typography: ["!font-light"],
      color: ["!bg-contrast-process-cyan !border-contrast-process-cyan"],
      focus: ["focus:ring-[1px] focus:!ring-contrast-process-cyan"]
    },

    "tai__button_root--tertiary--outlined": {
      color: [
        "bg-white !border-contrast-process-cyan !text-contrast-process-cyan hover:!bg-contrast-process-cyan hover:!text-white transition duration-200"
      ],
      focus: ["focus:ring-[1px] focus:!ring-contrast-process-cyan"]
    },

    "tai__button_root--tertiary--transparent": {
      color: ["!text-contrast-process-cyan"]
    },

    tai__button_label: {
      typography: ["!text-xs"]
    },
    tai__button_label_tertiary: {
      typography: ["!text-sm !font-medium"]
    }
  },
  preset: {
    root: (ctx: {
      attrs: TaiButtonProps;
      props: ButtonProps;
      context: ButtonPassThroughMethodOptions<unknown>["context"];
    }) => [
      "tai__button_root",
      {
        "tai__button_root--with-label": !!ctx.props.label
      },
      {
        "tai__button_root--transparent": !!ctx.attrs.transparent
      },
      {
        "tai__button_root--disabled":
          !!ctx.context.disabled && !ctx.props.outlined,

        "tai__button_root--primary":
          ctx.attrs.variant === "primary" && !ctx.props.outlined,

        "tai__button_root--outlined--disabled":
          !!ctx.context.disabled && ctx.props.outlined,

        "tai__button_root--primary--outlined":
          ctx.attrs.variant === "primary" && ctx.props.outlined,

        "tai__button_root--primary--transparent":
          ctx.attrs.variant === "primary" && ctx.attrs.transparent,

        "tai__button_root--secondary":
          ctx.attrs.variant === "secondary" && !ctx.props.outlined,

        "tai__button_root--secondary--outlined":
          ctx.attrs.variant === "secondary" && ctx.props.outlined,

        "tai__button_root--secondary--transparent":
          ctx.attrs.variant === "secondary" && ctx.attrs.transparent,

        "tai__button_root--tertiary":
          ctx.attrs.variant === "tertiary" && !ctx.props.outlined,

        "tai__button_root--tertiary--outlined":
          ctx.attrs.variant === "tertiary" && ctx.props.outlined,

        "tai__button_root--tertiary--transparent":
          ctx.attrs.variant === "tertiary" && ctx.attrs.transparent
      }
    ],
    label: (ctx: { attrs: TaiButtonProps; props: ButtonProps }) => [
      "tai__button_label",
      {
        tai__button_label_tertiary:
          ctx.attrs.variant === "tertiary" && !ctx.props.outlined
      }
    ]
  }
});
</script>

<template>
  <Button
    v-if="!props.permission || permissionResult?.allowed"
    v-tai-tooltip="{ value: tooltip, disabled: !tooltip }"
    data-testid="tai-button"
    v-bind="{
      ...$props,
      ...$attrs,
      unstyled: true
    }"
    :label="$props.label"
    :pt="preset"
    :pt-options="{ mergeProps: true }"
  >
    <template #icon v-if="props.loading">
      <ProgressSpinnerComponent />
    </template>
    <template #icon v-else-if="$props.icon">
      <IconComponent :name="$props.icon as IconName" />
    </template>
  </Button>
  <Button
    v-else
    data-testid="tai-button-permission-denied"
    v-bind="{
      ...$props,
      ...$attrs,
      unstyled: true,
      onClick: handleTrigger
    }"
    :label="$props.label"
    :pt="preset"
    :pt-options="{ mergeProps: true }"
  >
    <template #icon v-if="permissionResult?.allowed === false">
      <IconComponent :name="IconName.Lock" />
    </template>
  </Button>
  <PermissionGateComponent
    v-if="!!props.permission"
    ref="button-permission-gate"
    :config="props.permission"
  />
</template>

<style lang="scss" scoped></style>
